/*------------------------------------------------------------------------------
   About      : "Model Permissions" category tab — record-level (row) scoping
                 on top of the action grants from Pages & Actions / API
                 Permissions

   Lists BFF_FEATURES domains ("models") the selected role/user already holds
   at least one granted action in (selectModelsWithAssociation), then lets an
   admin attach attribute-based restriction rules (e.g. utility_id IN [...])
   scoping which records of that model the entity may see. The attribute and
   value pickers are driven by the backend's whitelist/lookup endpoints
   (getModelAttributes / getModelAttributeValues) rather than freeform text,
   so an admin can't type a typo'd attribute or a value that will never
   match. Restrictions on the same model are OR'd server-side; no rule means
   unrestricted access to whatever is already granted.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Autocomplete,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import { ChevronDown, ChevronRight, Trash2, Plus } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { RbacApis } from 'apis/rbacAPI';
import { selectModelsWithAssociation, RbacEntityType } from 'store/dataReducers/rbacSlice';
import { ModelRecordRestriction, ModelAttribute, ModelAttributeValue } from 'types/apis/rbacInterfaces';

const chipSx = { height: 18, fontSize: 11 } as const;

interface Draft {
  attribute: string | null;
  values: ModelAttributeValue[];
}

const emptyDraft: Draft = { attribute: null, values: [] };

export interface ModelPermissionsPanelProps {
  entityType: RbacEntityType;
  entityId: number | string;
  onSaved: () => void | Promise<void>;
}

export const ModelPermissionsPanel = ({ entityType, entityId, onSaved }: ModelPermissionsPanelProps) => {
  const { rc } = useGlobalConfig();
  const toast = useToast();

  const models = useSelector(selectModelsWithAssociation);

  const [restrictions, setRestrictions] = useState<ModelRecordRestriction[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [drafts, setDrafts] = useState<Map<string, Draft>>(new Map());
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const [attributeOptions, setAttributeOptions] = useState<Map<string, ModelAttribute[]>>(new Map());
  const [valueOptions, setValueOptions] = useState<Map<string, ModelAttributeValue[]>>(new Map());
  const [valuesLoading, setValuesLoading] = useState<Set<string>>(new Set());

  const loadRestrictions = async () => {
    setLoading(true);
    const resp = entityType === 'role'
      ? await RbacApis.getRoleModelRestrictions(rc, entityId)
      : await RbacApis.getUserModelRestrictions(rc, entityId);
    if (resp.success) setRestrictions(resp.data || []);
    else if (resp.error) toast.error(resp.error);
    setLoading(false);
  };

  useEffect(() => {
    loadRestrictions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  const toggleExpand = (model: { key: string }) => {
    const alreadyExpanded = expanded.has(model.key);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (alreadyExpanded) next.delete(model.key);
      else next.add(model.key);
      return next;
    });
    if (alreadyExpanded) return;

    if (!attributeOptions.has(model.key)) {
      RbacApis.getModelAttributes(rc, model.key).then((resp) => {
        if (resp.success) {
          setAttributeOptions((prev) => new Map(prev).set(model.key, resp.data?.attributes || []));
        } else if (resp.error) {
          toast.error(resp.error);
        }
      });
    }

    // Existing restriction chips show raw values (e.g. a utility_id) until their attribute's
    // value labels are loaded — pull a full page per attribute already in use so the chip can
    // render "Acme Utility" instead of "1".
    const attributesInUse = new Set(restrictionsFor(model.key).map((r) => r.attribute));
    attributesInUse.forEach((attribute) => fetchValueOptions(model.key, attribute, undefined, 200));
  };

  const draftFor = (model: string): Draft => drafts.get(model) || emptyDraft;

  const setDraft = (model: string, patch: Partial<Draft>) =>
    setDrafts((prev) => {
      const next = new Map(prev);
      next.set(model, { ...draftFor(model), ...patch });
      return next;
    });

  const fetchValueOptions = (model: string, attribute: string, q?: string, perPage?: number) => {
    const cacheKey = `${model}:${attribute}`;
    if (!q && (valueOptions.has(cacheKey) || valuesLoading.has(cacheKey))) return;
    setValuesLoading((prev) => new Set(prev).add(cacheKey));
    RbacApis.getModelAttributeValues(rc, model, attribute, { q, per_page: perPage }).then((resp) => {
      if (resp.success) {
        setValueOptions((prev) => new Map(prev).set(cacheKey, resp.data?.values || []));
      } else if (resp.error) {
        toast.error(resp.error);
      }
      setValuesLoading((prev) => {
        const next = new Set(prev);
        next.delete(cacheKey);
        return next;
      });
    });
  };

  const handleAttributeChange = (model: string, attribute: string | null) => {
    setDraft(model, { attribute, values: [] });
    if (attribute) fetchValueOptions(model, attribute);
  };

  const handleAdd = async (model: string) => {
    const draft = draftFor(model);
    if (!draft.attribute || draft.values.length === 0) return;

    setBusyKey(`add:${model}`);
    const body = { model, attribute: draft.attribute, values: draft.values.map((v) => String(v.value)) };
    const resp = entityType === 'role'
      ? await RbacApis.createRoleModelRestriction(rc, entityId, body)
      : await RbacApis.createUserModelRestriction(rc, entityId, body);

    if (resp.success) {
      setDrafts((prev) => {
        const next = new Map(prev);
        next.delete(model);
        return next;
      });
      await loadRestrictions();
      await onSaved();
      toast.success('Restriction added');
    } else if (resp.error) {
      toast.error(resp.error);
    }
    setBusyKey(null);
  };

  const handleDelete = async (restriction: ModelRecordRestriction) => {
    setBusyKey(`delete:${restriction.id}`);
    const resp = entityType === 'role'
      ? await RbacApis.deleteRoleModelRestriction(rc, entityId, restriction.id)
      : await RbacApis.deleteUserModelRestriction(rc, entityId, restriction.id);

    if (resp.success) {
      await loadRestrictions();
      await onSaved();
      toast.success('Restriction removed');
    } else if (resp.error) {
      toast.error(resp.error);
    }
    setBusyKey(null);
  };

  const restrictionsFor = (model: string) => restrictions.filter((r) => r.model === model);

  const attributeLabel = (model: string, key: string) =>
    (attributeOptions.get(model) || []).find((a) => a.key === key)?.label || key;

  const valueLabel = (model: string, attribute: string, value: string) =>
    (valueOptions.get(`${model}:${attribute}`) || []).find((v) => String(v.value) === value)?.label ?? value;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Models this {entityType} already has page/API access to. Add a restriction to scope which records it may see.
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pb: 1 }}>
        {loading && <CircularProgress size={20} sx={{ m: 1 }} />}

        {!loading && models.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No models associated with this {entityType} yet — grant it a page or API permission first.
          </Typography>
        )}

        {!loading && models.map((model) => {
          const isExpanded = expanded.has(model.key);
          const modelRestrictions = restrictionsFor(model.key);
          const draft = draftFor(model.key);
          const adding = busyKey === `add:${model.key}`;
          const canRestrict = model.listActionGranted;
          const attributes = attributeOptions.get(model.key) || [];
          const valueCacheKey = draft.attribute ? `${model.key}:${draft.attribute}` : '';
          const availableValues = valueOptions.get(valueCacheKey) || [];
          const loadingValues = valuesLoading.has(valueCacheKey);

          return (
            <Box key={model.key} sx={{ mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25 }}>
                <IconButton size="small" onClick={() => toggleExpand(model)} sx={{ p: 0.25 }}>
                  {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </IconButton>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{model.label}</Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${model.grantedActions}/${model.totalActions} actions`}
                  sx={{ ...chipSx, color: 'text.secondary' }}
                />
                {modelRestrictions.length > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="primary"
                    label={`${modelRestrictions.length} restriction${modelRestrictions.length > 1 ? 's' : ''}`}
                    sx={chipSx}
                  />
                )}
                {!canRestrict && (
                  <Chip size="small" variant="outlined" label="list action not granted" sx={{ ...chipSx, color: 'text.secondary' }} />
                )}
              </Box>

              {isExpanded && (
                <Box sx={{ pl: 5, pb: 1 }}>
                  {modelRestrictions.map((restriction) => (
                    <Box key={restriction.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25 }}>
                      <Chip size="small" variant="outlined" label={attributeLabel(model.key, restriction.attribute)} sx={chipSx} />
                      <Typography variant="body2" color="text.secondary">in</Typography>
                      {restriction.values.map((value) => (
                        <Chip key={value} size="small" label={valueLabel(model.key, restriction.attribute, value)} sx={chipSx} />
                      ))}
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(restriction)}
                        disabled={busyKey === `delete:${restriction.id}`}
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    </Box>
                  ))}

                  {modelRestrictions.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      No restrictions — this {entityType} sees every record it has action access to.
                    </Typography>
                  )}

                  {canRestrict ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Autocomplete
                        size="small"
                        options={attributes}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.key === v.key}
                        value={attributes.find((a) => a.key === draft.attribute) || null}
                        onChange={(_, option) => handleAttributeChange(model.key, option?.key || null)}
                        sx={{ width: 200 }}
                        renderInput={(params) => <TextField {...params} placeholder="Attribute" />}
                      />
                      <Autocomplete
                        multiple
                        size="small"
                        disabled={!draft.attribute}
                        loading={loadingValues}
                        options={availableValues}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        value={draft.values}
                        onChange={(_, options) => setDraft(model.key, { values: options })}
                        onInputChange={(_, inputValue) => {
                          if (draft.attribute) fetchValueOptions(model.key, draft.attribute, inputValue);
                        }}
                        sx={{ flexGrow: 1 }}
                        renderInput={(params) => <TextField {...params} placeholder="Values" />}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={adding ? <CircularProgress size={12} /> : <Plus size={14} />}
                        onClick={() => handleAdd(model.key)}
                        disabled={adding || !draft.attribute || draft.values.length === 0}
                      >
                        Add
                      </Button>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      This {entityType} doesn't hold the list action this model's restrictions actually filter — grant that action
                      first, or any rule here would silently do nothing.
                    </Typography>
                  )}
                </Box>
              )}
              <Divider sx={{ mt: 1 }} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
